import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { downloadFile } from "@/lib/download";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Database, Download, Upload, Loader2 } from "lucide-react";
import RestoreBackupDialog from "./RestoreBackupDialog";

function BackupRow({ b, onDownload, onRestore }) {
  return (
    <tr className="border-b border-slate-50" data-testid="backup-row">
      <td className="py-2 font-mono-data text-slate-700">{b.filename}</td>
      <td className="py-2"><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${b.kind === "auto" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>{b.kind}</span></td>
      <td className="py-2 text-slate-500">{b.record_total}</td>
      <td className="py-2 text-slate-500">{b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : ""}</td>
      <td className="py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="outline" className="h-7 text-[11px] mr-1" onClick={() => onDownload(b)} data-testid={`backup-download-${b.filename}`}><Download className="h-3 w-3 mr-1" />Download</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onRestore(b)} data-testid={`backup-restore-${b.filename}`}><Upload className="h-3 w-3 mr-1" />Restore</Button>
      </td>
    </tr>
  );
}

// Server-side .xlsx backups: nightly auto + on-demand, with download / guarded restore.
export default function BackupPanel({ onRestored }) {
  const [backups, setBackups] = useState([]);
  const [lastBackupAt, setLastBackupAt] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/backups`); setBackups(r?.backups || []); setLastBackupAt(r?.last_backup_at || null); }
    catch (e) { logger.error("Failed to load backups:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    setBackingUp(true);
    try { const r = await api.post(`/admin/backups`); toast.success(r?.message || "Backup created"); load(); }
    catch (e) { logger.error("Backup failed:", e); toast.error("Backup failed"); }
    finally { setBackingUp(false); }
  };
  const restore = async (id) => {
    try { const r = await api.post(`/admin/backups/${id}/restore`); toast.success(r?.message || "Restored"); onRestored?.(); load(); }
    catch (e) { logger.error("Restore failed:", e); toast.error("Restore failed"); throw e; }
  };
  const download = async (b) => {
    try { await downloadFile(`${API}/admin/backups/${b.id}/download`, b.filename || "backup.xlsx"); }
    catch (e) { logger.error("Backup download failed:", e); toast.error("Download failed"); }
  };

  return (
    <div className="xl:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="database-backup">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          <h3 className="font-display text-base font-semibold text-slate-900">Database Backup</h3>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${lastBackupAt ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`} data-testid="backup-health">
            {lastBackupAt ? `Last backup: ${new Date(lastBackupAt).toLocaleString("en-IN")}` : "No backups yet"}
          </span>
        </div>
        <Button size="sm" className="h-8 text-[11px] bg-blue-600 hover:bg-blue-700" disabled={backingUp} onClick={backupNow} data-testid="backup-now-btn">
          {backingUp ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1" />} Backup Now
        </Button>
      </div>
      <p className="text-xs text-slate-400 mb-3">Automatic backups run daily at midnight and are saved to the server's <span className="font-mono-data">/backups</span> folder.</p>
      {backups.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="backup-empty">No backups yet — one runs automatically at midnight, or click “Backup Now”.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 font-semibold">File</th><th className="py-2 font-semibold">Type</th><th className="py-2 font-semibold">Records</th><th className="py-2 font-semibold">Created</th><th className="py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="backup-list">
              {backups.map((b) => <BackupRow key={b.id} b={b} onDownload={download} onRestore={setRestoreTarget} />)}
            </tbody>
          </table>
        </div>
      )}
      <RestoreBackupDialog backup={restoreTarget} onClose={() => setRestoreTarget(null)} onRestore={restore} />
    </div>
  );
}
